import React, { useState } from 'react';
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { TableEditColumn } from '@devexpress/dx-react-grid-material-ui';

import PropTypes from 'prop-types';

/**
 * KebabCommandCell
 *
 * A custom cellComponent for TableEditColumn that collapses the Edit and Delete
 * command buttons into a kebab (⋮) dropdown menu.
 *
 * The grid passes already-wired command buttons as `children`. Each child has
 * props: { id: 'edit' | 'delete' | ..., onExecute: () => void }.
 * We extract those onExecute callbacks and hook them into the menu items.
 *
 * The "Add" command is rendered in the *header* cell (not data cells), so it
 * is completely unaffected by this component and works as normal.
 */
const KebabCommandCell = ({ children, onPopupEditStart, ...rest }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (event) => {
    event?.stopPropagation?.();
    setAnchorEl(null);
  };

  // Extract onExecute callbacks from the grid-provided command children.
  // Each child is a rendered command component with props: { id, onExecute }
  const commandMap = {};
  React.Children.forEach(children, (child) => {
    if (child && child.props && child.props.id) {
      commandMap[child.props.id] = child.props.onExecute;
    }
  });

  const handleEdit = (event) => {
    event.stopPropagation();
    if (commandMap['edit']) {
      onPopupEditStart();
      commandMap['edit']();
    }
    handleClose();
  };

  const handleDelete = (event) => {
    event.stopPropagation();
    if (commandMap['delete']) {
      commandMap['delete']();
    }
    handleClose();
  };

  // Keep native rendering for commit/cancel commands and disabled rows. This
  // allows the kebab menu to coexist with the table's inline-editing behavior.
  const hasActions = commandMap['edit'] || commandMap['delete'];
  if (!hasActions) {
    // Edit-mode row: render commit + cancel stacked vertically to fit in the 60px column.
    return (
      <TableEditColumn.Cell {...rest}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '2px 0'
          }}
        >
          {children}
        </div>
      </TableEditColumn.Cell>
    );
  }

  return (
    <TableEditColumn.Cell
      {...rest}
      onClick={(event) => event.stopPropagation()}
      onDoubleClick={(event) => event.stopPropagation()}
      style={{
        ...rest.style,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '9px'
      }}
    >
      <IconButton aria-label="row actions" onClick={handleClick} size="small">
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {commandMap['edit'] && (
          <MenuItem onClick={handleEdit}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {commandMap['delete'] && (
          <MenuItem onClick={handleDelete}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </TableEditColumn.Cell>
  );
};

export default KebabCommandCell;

KebabCommandCell.propTypes = {
  children: PropTypes.any,
  onPopupEditStart: PropTypes.func
};

KebabCommandCell.defaultProps = { onPopupEditStart: () => {} };
